/** 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 * Source: qubepro-core.
 */

/** A customer membership cannot inherit a previous platform-management projection. */
export function normalizeUserAuthority(input: {
  membershipId?: unknown;
  userKind?: unknown;
  platformStaff?: unknown;
  isAdmin?: unknown;
  permissions?: unknown;
  role?: unknown;
  roles?: unknown;
  customerId?: unknown;
  membershipStatus?: unknown;
  tenantRole?: unknown;
  managementAccess?: unknown;
}) {
  const membershipScoped = input.membershipId != null && String(input.membershipId) !== '';
  const roles = Array.isArray(input.roles) ? input.roles.map(String) : [];
  const permissions = Array.isArray(input.permissions) ? input.permissions.map(String) : [];
  const candidate = input.managementAccess as { kind?: unknown; customerId?: unknown } | null;
  const managementAccess = membershipScoped && input.userKind === 'INTERNAL'
    && input.membershipStatus === 'ACTIVE' && ['TENANTOWNER', 'TENANTADMIN'].includes(String(input.tenantRole))
    && candidate?.kind === 'TENANT_MANAGEMENT' && /^[1-9][0-9]*$/.test(String(input.customerId))
    && candidate.customerId === String(input.customerId)
    ? { kind: 'TENANT_MANAGEMENT' as const, customerId: String(input.customerId) } : null;
  return {
    platformStaff: !membershipScoped && input.platformStaff === true,
    isAdmin: !membershipScoped && input.userKind === 'INTERNAL' && input.isAdmin === true,
    permissions: membershipScoped ? permissions.filter(code => code !== '*') : permissions,
    role: membershipScoped && !/^(10|11|12)$/.test(String(input.role)) ? undefined : input.role as string | number | undefined,
    roles: membershipScoped ? roles.filter(role => /^(10|11|12)$/.test(role)) : roles,
    ...(managementAccess ? { managementAccess } : {}),
  };
}

/** UI visibility follows effective server permissions. This never creates a grant
 * from a numeric role, staff flag or tenant-management descriptor. */
export type UserAccessAuthority = {
  identityId?: unknown;
  membershipId?: unknown;
  membershipStatus?: unknown;
  tenantRole?: unknown;
  tenantId?: unknown;
  customerId?: unknown;
  userKind?: unknown;
  isAdmin?: unknown;
  role?: unknown;
  permissions?: unknown;
};

export function canAccessUserPermission(user: UserAccessAuthority | null | undefined, permission: string): boolean {
  if (!user || user.userKind !== 'INTERNAL' || !permission || permission.includes('*')) return false;
  const platformAdmin = !user.membershipId && user.isAdmin === true;
  return platformAdmin || (Array.isArray(user.permissions) && user.permissions.includes(permission));
}

export function resolveUserRoleAccess(user: UserAccessAuthority | null | undefined) {
  // Product grants and customer Console navigation are separate authorities.
  const isFullAccess = user?.userKind === 'INTERNAL' && !user.membershipId && user.isAdmin === true;
  const positiveId = (value: unknown) => /^[1-9][0-9]*$/.test(String(value));
  const tenantId = user?.tenantId ?? user?.customerId;
  const sameTenant = user?.tenantId == null || user?.customerId == null
    || String(user.tenantId) === String(user.customerId);
  const canOpenAdminConsole = isFullAccess || (user?.userKind === 'INTERNAL'
    && positiveId(user.identityId) && positiveId(user.membershipId) && positiveId(tenantId)
    && sameTenant && user.membershipStatus === 'ACTIVE'
    && ['TENANTOWNER', 'TENANTADMIN'].includes(String(user.tenantRole)));
  const roleNum = user?.role != null && Number.isFinite(Number(user.role)) ? Number(user.role) : null;
  const canViewManagement = isFullAccess || ['dashboard:read', 'company:read', 'user:read', 'role:read', 'organization:read']
    .some(permission => canAccessUserPermission(user, permission));
  return {
    roleNum: isFullAccess ? 1 : roleNum,
    isFullAccess,
    canOpenAdminConsole,
    isUserSettingsOnly: !canViewManagement,
    canViewManagement,
    canViewSettings: true,
    canViewSystemSettings: canAccessUserPermission(user, 'settings:system'),
    canViewUserSettings: true,
  };
}
